/**
 * Обёртка над Telegram WebApp BiometricManager
 * https://core.telegram.org/bots/webapps#biometricmanager
 *
 * Поддерживается только на Telegram-клиентах iOS 7.2+ и Android 7.2+.
 * На десктопе / Web-версии API нет — методы вернут null / false, и мы
 * тихо деградируем к PIN-у.
 *
 * Флаг «биометрия подключена» дублируем в localStorage: BiometricManager
 * умеет только `isBiometricTokenSaved`, а этот флаг нам нужен ещё до
 * `init()` — например, чтобы решить показывать ли lock-screen сразу
 * на первом рендере.
 */

const ENROLL_KEY = "zero_biometric_enrolled";
const UNLOCK_SESSION_KEY = "zero_session_unlocked";

/** Минимальный тип для BiometricManager — официальных типов в SDK нет. */
export interface BiometricManager {
  isInited: boolean;
  isBiometricAvailable: boolean;
  biometricType: "finger" | "face" | "unknown";
  isAccessRequested: boolean;
  isAccessGranted: boolean;
  isBiometricTokenSaved: boolean;
  deviceId: string;
  init(cb?: () => void): BiometricManager;
  requestAccess(
    params: { reason?: string },
    cb?: (granted: boolean) => void,
  ): BiometricManager;
  authenticate(
    params: { reason?: string },
    cb?: (ok: boolean, token?: string) => void,
  ): BiometricManager;
  updateBiometricToken(
    token: string,
    cb?: (ok: boolean) => void,
  ): BiometricManager;
  openSettings(): BiometricManager;
}

/** Достаём BiometricManager у Telegram.WebApp; null если фича не поддерживается. */
export function getBiometricManager(): BiometricManager | null {
  try {
    const tg: unknown = (window as unknown as { Telegram?: { WebApp?: unknown } })
      .Telegram?.WebApp;
    const bm = (tg as { BiometricManager?: BiometricManager })?.BiometricManager;
    return bm ?? null;
  } catch {
    return null;
  }
}

/** Ленивый init — резолвится один раз, потом BiometricManager помнит состояние. */
export async function ensureBiometricInited(): Promise<BiometricManager | null> {
  const bm = getBiometricManager();
  if (!bm) return null;
  if (bm.isInited) return bm;
  await new Promise<void>((resolve) => {
    try {
      bm.init(() => resolve());
    } catch {
      resolve();
    }
  });
  return bm;
}

/** Есть ли на этом устройстве поддержка биометрии (Face ID / отпечаток). */
export async function hasBiometricSupport(): Promise<boolean> {
  const bm = await ensureBiometricInited();
  return Boolean(bm?.isBiometricAvailable);
}

/** Русское название типа биометрии для UI. */
export function biometricTypeLabel(
  type: BiometricManager["biometricType"] | undefined,
): string {
  switch (type) {
    case "face":
      return "Face ID";
    case "finger":
      return "Touch ID / отпечаток";
    default:
      return "биометрия";
  }
}

/**
 * Полный флоу подключения биометрии: init → requestAccess → сохранить
 * PIN в защищённом хранилище устройства (Telegram шифрует его локально).
 * Возвращает `true` при успехе.
 */
export async function enrollBiometric(pin: string): Promise<boolean> {
  const bm = await ensureBiometricInited();
  if (!bm || !bm.isBiometricAvailable) return false;

  // 1) Запрашиваем разрешение у пользователя (нативный system prompt).
  const granted = await new Promise<boolean>((resolve) => {
    try {
      bm.requestAccess(
        {
          reason:
            "Разрешите использовать биометрию для быстрого входа в приложение",
        },
        (ok) => resolve(ok),
      );
    } catch {
      resolve(false);
    }
  });
  if (!granted) return false;

  // 2) Сохраняем PIN как биометрический токен. Telegram хранит его
  //    в Keychain / Android Keystore, привязанным к биометрии.
  const saved = await new Promise<boolean>((resolve) => {
    try {
      bm.updateBiometricToken(pin, (ok) => resolve(ok));
    } catch {
      resolve(false);
    }
  });
  if (!saved) return false;

  try {
    localStorage.setItem(ENROLL_KEY, "1");
  } catch {
    // localStorage может быть недоступен — не критично.
  }
  return true;
}

/**
 * Запрос биометрической аутентификации. Возвращает `{ ok, pin? }`.
 * `pin` — тот же PIN, который был сохранён при enrollBiometric.
 */
export async function authenticateBiometric(): Promise<{
  ok: boolean;
  pin?: string;
}> {
  const bm = await ensureBiometricInited();
  if (!bm || !bm.isBiometricAvailable) return { ok: false };
  if (!bm.isBiometricTokenSaved) return { ok: false };

  return new Promise((resolve) => {
    try {
      bm.authenticate(
        { reason: "Подтвердите вход в кошелёк" },
        (ok, token) => resolve({ ok, pin: token }),
      );
    } catch {
      resolve({ ok: false });
    }
  });
}

/** Записан ли флаг «биометрия подключена» в localStorage. */
export function isBiometricEnrolled(): boolean {
  try {
    return localStorage.getItem(ENROLL_KEY) === "1";
  } catch {
    return false;
  }
}

/** Снять флаг (например при разлогине или отключении). */
export function clearBiometricEnrolled(): void {
  try {
    localStorage.removeItem(ENROLL_KEY);
  } catch {
    // localStorage может быть недоступен
  }
}

/**
 * Отметка «сессия разблокирована» — живёт только пока WebApp открыт.
 * Мы не используем localStorage, чтобы каждое новое открытие требовало
 * биометрии / PIN-а заново (это, собственно, вся суть lock-screen'а).
 */
export function markSessionUnlocked(): void {
  try {
    sessionStorage.setItem(UNLOCK_SESSION_KEY, "1");
  } catch {
    // sessionStorage может быть недоступен
  }
}

export function isSessionUnlocked(): boolean {
  try {
    return sessionStorage.getItem(UNLOCK_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}
